// SocialPage — orchestrator dei tab della sezione Social.
// I sotto-componenti sono in /app/frontend/src/pages/social/*.jsx
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { Calendar as CalIcon, BarChart3, Link2, MessageSquare, Megaphone, TrendingUp, AlertTriangle, Trophy, Activity, Sparkles } from "lucide-react";
import { PLATFORM_BY_KEY } from "./social/common";
import { AnalyticsTab } from "./social/AnalyticsTab";
import { InboxTab } from "./social/InboxTab";
import { CalendarTab } from "./social/CalendarTab";
import { AdsTab } from "./social/AdsTab";
import { StatsTab } from "./social/StatsTab";
import { ConnectionsTab } from "./social/ConnectionsTab";
import { HealthTab } from "./social/HealthTab";
import { RankingsTab } from "./social/RankingsTab";

export default function SocialPage() {
  const [clients, setClients] = useState([]);
  const [accountsByClient, setAccountsByClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("health");
  // Client selezionato condiviso tra tutte le tab (Analytics/Inbox/Planning/Ads).
  // Persistito in sessionStorage per sopravvivere anche a un refresh soft.
  const [selectedClientId, setSelectedClientId] = useState(() => sessionStorage.getItem("social:selectedClientId") || "");

  useEffect(() => {
    if (selectedClientId) sessionStorage.setItem("social:selectedClientId", selectedClientId);
    else sessionStorage.removeItem("social:selectedClientId");
  }, [selectedClientId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const cRes = await apiFetch("/clients");
      if (!cRes.ok) { toast.error("Errore caricamento clienti"); return; }
      const cs = await cRes.json();
      setClients(cs);
      const obj = {};
      await Promise.all(cs.map(async (c) => {
        try {
          const r = await apiFetch(`/zernio/accounts/${c.client_id}`);
          if (r.ok) {
            const data = await r.json();
            obj[c.client_id] = data.accounts || [];
          }
        } catch { /* ignore */ }
      }));
      setAccountsByClient(obj);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get("zernio_ok");
    if (ok) {
      toast.success(`${PLATFORM_BY_KEY[ok]?.label || ok} collegato!`);
      window.history.replaceState({}, "", "/social");
      fetchAll();
    } else if (params.get("zernio_error")) {
      toast.error("Errore OAuth: " + params.get("zernio_error"));
      window.history.replaceState({}, "", "/social");
    } else if (params.get("drive_ok")) {
      toast.success("Google Drive collegato!");
      window.history.replaceState({}, "", "/social");
    } else if (params.get("drive_error")) {
      toast.error("Errore Drive: " + params.get("drive_error"));
      window.history.replaceState({}, "", "/social");
    }
  }, []);

  return (
    <div className="social-page min-h-[calc(100vh-4rem)] space-y-5">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 sm:px-6 py-3.5 shadow-[0_12px_35px_-22px_rgba(15,23,42,0.35)]">
        <div className="absolute inset-y-0 right-0 w-72 bg-gradient-to-l from-indigo-50/80 to-transparent pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-500 text-white shadow-lg shadow-indigo-500/20 flex items-center justify-center">
            <Activity className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight" data-testid="social-page-title">Social Hub</h1>
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                <Sparkles className="h-3 w-3" /> Workspace
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Account, contenuti e performance social in un unico spazio.</p>
          </div>
          <div className="ml-auto hidden md:flex items-center gap-2 text-[11px] font-medium text-slate-500">
            <span className="rounded-full border bg-white px-3 py-1.5 shadow-sm">Zernio</span>
            <span className="rounded-full border bg-white px-3 py-1.5 shadow-sm">Metricool</span>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="w-full justify-start gap-1 bg-slate-900 p-1.5 h-auto text-white flex-wrap rounded-2xl border border-slate-800 shadow-[0_12px_28px_-14px_rgba(15,23,42,0.65)]" data-testid="social-tabs">
          <TabsTrigger value="health" data-testid="tab-health" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><AlertTriangle className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Salute Clienti</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><BarChart3 className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Analytics</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><MessageSquare className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Inbox</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><CalIcon className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Planning</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-ads" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><Megaphone className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Ads</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><TrendingUp className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Statistiche Metricool</TabsTrigger>
          <TabsTrigger value="rankings" data-testid="tab-rankings" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><Trophy className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Classifiche</TabsTrigger>
          <TabsTrigger value="connections" data-testid="tab-connections" className="group rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md text-slate-300 hover:bg-white/10 hover:text-white transition-all"><Link2 className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" /> Connessioni</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="m-0">
          <HealthTab />
        </TabsContent>
        <TabsContent value="analytics" className="m-0">
          <AnalyticsTab clients={clients} selectedClientId={selectedClientId} onClientChange={setSelectedClientId} />
        </TabsContent>
        <TabsContent value="inbox" className="m-0">
          <InboxTab clients={clients} accountsByClient={accountsByClient} selectedClientId={selectedClientId} onClientChange={setSelectedClientId} />
        </TabsContent>
        <TabsContent value="connections" className="m-0">
          <ConnectionsTab clients={clients} accountsByClient={accountsByClient} fetchAll={fetchAll} loading={loading} />
        </TabsContent>
        <TabsContent value="calendar" className="m-0">
          <CalendarTab clients={clients} accountsByClient={accountsByClient} fetchAll={fetchAll} selectedClientId={selectedClientId} onClientChange={setSelectedClientId} />
        </TabsContent>
        <TabsContent value="ads" className="m-0">
          <AdsTab clients={clients} selectedClientId={selectedClientId} onClientChange={setSelectedClientId} />
        </TabsContent>
        <TabsContent value="stats" className="m-0">
          <StatsTab clients={clients} />
        </TabsContent>
        <TabsContent value="rankings" className="m-0">
          <RankingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
